import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Tab,
  Tabs,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemText,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  InputAdornment,
  Snackbar,
  Popover,
  ListItemButton,
  Tooltip,
} from '@mui/material';
import {
  Person,
  Business,
  Notifications,
  Security,
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  Email,
  Phone,
  LocationOn,
  Description,
  Verified,
  Warning,
  ArrowBack,
  Image,
  Add,
  Delete,
  AccountBalanceWallet,
  Storefront,
  Visibility,
  VisibilityOff,
  ContentCopy,
  Share,
  Search as SearchIcon,
  ArrowDropDown as ArrowDropDownIcon,
  LockOutlined,
} from '@mui/icons-material';
import merchantAPI, { merchantAuthAPI, referralCodeAPI } from '../../../services/merchantAPI';
import { getMerchantSubscription } from '../../../services/subscriptionAPI';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { masterDataConfigAPI } from '../../../services/api'; // Import masterDataConfigAPI
import { API_URLS } from '../../../config/apiConfig'; // Import API configuration
import FlutterServicesAPI from '../../../services/flutterServices'; // Import FlutterServicesAPI for mobile permissions
import { AutoCloseAlert } from '../../Common';
import SecureImage from '../../Common/SecureImage';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import useRouteMeta from '../../../hooks/useRouteMeta';
import { getRegisterUrl } from '../../../utils/environmentUtils';
const MerchantProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  useRouteMeta();
  const profileInfoRef = useRef(null);
  const originalContactRef = useRef({ email: '', whatsapp_number: '', whatsapp_code: '' });
  const [activeTab, setActiveTab] = useState(0);
  const [profile, setProfile] = useState({});
  const [notifications, setNotifications] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [brandDialogError, setBrandDialogError] = useState(''); // Separate error state for brand dialog
  const [fieldErrors, setFieldErrors] = useState({});
  const [emailVerification, setEmailVerification] = useState({ otpSent: false, otp: '', verified: false, token: '', loading: false });
  const [mobileVerification, setMobileVerification] = useState({ otpSent: false, otp: '', verified: false, token: '', loading: false });
  const [hasActiveSub, setHasActiveSub] = useState(false);

  // Password dialog state - removing this in favor of inline form
  const [passwordError, setPasswordError] = useState('');

  // Password fields state - keep these for inline form
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Password visibility state - keep these for inline form
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // State to control visibility of password form
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = useState(true);

  // Location data state
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Brand Logo state
  const [brandLogos, setBrandLogos] = useState([]);
  const [brandLogoDialog, setBrandLogoDialog] = useState(false);
  const [editingBrandLogo, setEditingBrandLogo] = useState(null);
  const [brandLogoData, setBrandLogoData] = useState({
    brand_name: '',
    brand_logo_path: ''
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(false); // Add permission check state

  // Background removal and enhancement options
  const [backgroundRemovalOptions, setBackgroundRemovalOptions] = useState({
    removeBackground: true,  // Default to enabled
    enhanceImage: true,      // Default to enabled
    sensitivity: 1.0
  });

  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedImageVariant, setSelectedImageVariant] = useState(0); // 0 = original, 1-3 = processed variants

  // Individual variant progress state
  const [variantProgress, setVariantProgress] = useState({});
  const [variantStatus, setVariantStatus] = useState({}); // 'loading', 'completed', 'error'

  // WhatsApp auto-fill state
  const [useSameAsMobile, setUseSameAsMobile] = useState(false);

  // Country codes for phone numbers - will be loaded from API
  const [countryCodes, setCountryCodes] = useState([]);

  // Referral code state - stores the merchant's assigned referral code
  const [referralCode, setReferralCode] = useState(null);
  // Loading state for referral code API call
  const [referralCodeLoading, setReferralCodeLoading] = useState(false);
  // Success state for copy to clipboard operation
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const { hasPermission, loading: permissionLoading } = useMerchantPermissions();
  const canViewProfile = hasPermission('Merchant Profile', 'view');
  const canEditProfile = hasPermission('Merchant Profile', 'edit');
  const canManageNotifications = hasPermission('Merchant Profile', 'manage_notifications');
  const canSecurity = hasPermission('Merchant Profile', 'change_password');
  const canViewBrand = hasPermission('Brand Management', 'view');
  const canCreateBrand = hasPermission('Brand Management', 'create');
  const canEditBrand = hasPermission('Brand Management', 'update');
  const canDeleteBrand = hasPermission('Brand Management', 'delete');

  const [editMode, setEditMode] = useState(false);

  // Share dialog state
  const [shareDialog, setShareDialog] = useState(false);

  // Delete account state
  const [deleteAccountDialog, setDeleteAccountDialog] = useState(false);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);

  // Delete brand logo state
  const [deleteBrandLogoDialog, setDeleteBrandLogoDialog] = useState(false);
  const [brandLogoToDelete, setBrandLogoToDelete] = useState(null);
  const [deleteBrandLogoLoading, setDeleteBrandLogoLoading] = useState(false);

  const [whatsappCountryAnchorEl, setWhatsappCountryAnchorEl] = useState(null);
  const [whatsappCountrySearch, setWhatsappCountrySearch] = useState('');
  const whatsappSearchInputRef = useRef(null);

  // Helper function to construct proper image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';

    // For merchant brand logos, use the authenticated API endpoint
    if (imagePath.startsWith('uploads/merchant_brand_logos/')) {
      // Extract merchant_id and filename from the path
      // Path format: uploads/merchant_brand_logos/{merchant_id}/{filename}
      const parts = imagePath.split('/');
      if (parts.length >= 4) {
        const merchant_id = parts[2];
        const filename = parts.slice(3).join('/'); // Handle filenames with slashes
        return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/merchant_brand_logos/${merchant_id}/${filename}`;
      }
    }

    // For brand logos, use the authenticated API endpoint
    if (imagePath.startsWith('uploads/brand_logos/')) {
      const filename = imagePath.replace('uploads/brand_logos/', '');
      return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/brand_logos/${filename}`;
    }

    // For other uploads, use the authenticated API endpoint
    if (imagePath.startsWith('uploads/')) {
      return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/${imagePath.replace('uploads/', '')}`;
    }

    // Fallback: add the uploads prefix and use authenticated endpoint
    return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/${imagePath}`;
  };

  // Validation functions
  const MAX_FULL_NAME_LEN = 100;
  const MAX_COMPANY_NAME_LEN = 100;

  const isValidProfileEmail = (email) => {
    const s = String(email || '').trim();
    if (!s || s.length > 254) return false;
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(s);
  };

  const normalizeLocalPhoneDigits = (raw) =>
    String(raw || '').replace(/[\s\u00A0]/g, '').replace(/-/g, '');

  const isIndiaDialCode = (code) => String(code || '').trim() === '+91';

  const validatePhone = (phone) => /^[6-9]\d{9}$/.test(phone);
  const validateWhatsApp = (digits, dialCode) => {
    if (!digits) return false;
    const n = normalizeLocalPhoneDigits(digits);
    if (!/^\d+$/.test(n)) return false;
    if (isIndiaDialCode(dialCode)) return /^[6-9]\d{9}$/.test(n);
    return /^\d{5,15}$/.test(n);
  };
  const validateGST = (gst) => {
    if (!gst) {
      return false;
    }

    // Remove spaces and convert to uppercase
    const cleanGST = gst.trim().toUpperCase();

    // Check basic structure: 15 characters
    if (cleanGST.length !== 15) {
      return false;
    }

    // Check format: 2 digits + 5 letters + 4 digits + 1 letter + 1 digit/letter + Z + 1 digit/letter
    const pattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!pattern.test(cleanGST)) {
      return false;
    }

    // Validate state code (first 2 digits)
    const stateCode = cleanGST.substring(0, 2);
    const validStateCodes = [
      '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20',
      '21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37'
    ];
    if (!validStateCodes.includes(stateCode)) {
      return false;
    }

    // Validate PAN format (positions 3-12: 5 letters + 4 digits + 1 letter)
    const panPart = cleanGST.substring(2, 12);
    const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panPattern.test(panPart)) {
      return false;
    }

    // Validate entity code (position 13: 1 digit/letter)
    const entityCode = cleanGST.charAt(12);
    if (!/^[0-9A-Z]$/.test(entityCode)) {
      return false;
    }

    // Validate default 'Z' character (position 14)
    if (cleanGST.charAt(13) !== 'Z') {
      return false;
    }

    // Validate checksum digit (position 15: 1 digit/letter)
    const checksum = cleanGST.charAt(14);
    if (!/^[0-9A-Z]$/.test(checksum)) {
      return false;
    }

    // Additional validation: Check if PAN is not all same characters
    const panLetters = panPart.substring(0, 5);
    if (new Set(panLetters).size === 1) {
      return false;
    }

    return true;
  };
  const validatePincode = (pincode) => /^[1-9][0-9]{5}$/.test(pincode);

  // Strip whitespace so pasted OTPs match backend exact match (avoids 400 Invalid OTP).
  const normalizeOtpInput = (otp) => String(otp ?? '').replace(/\s+/g, '').trim();

  const validateFields = (profile) => {
    const errors = {};

    // Keep mandatory fields aligned with register page
    const nameTrim = String(profile.name || '').trim();
    const companyTrim = String(profile.company_name || '').trim();
    const emailTrim = String(profile.email || '').trim();
    const whatsappDigits = normalizeLocalPhoneDigits(profile.whatsapp_number);
    const whatsappCode = String(profile.whatsapp_code || '').trim() || '+91';

    if (!nameTrim) errors.name = 'Full name is required';
    if (!companyTrim) errors.company_name = 'Company name is required';
    if (!emailTrim) errors.email = 'Email address is required';
    else if (!isValidProfileEmail(emailTrim)) errors.email = 'Please enter a valid email address';

    if (nameTrim && nameTrim.length > MAX_FULL_NAME_LEN) {
      errors.name = `Full name cannot exceed ${MAX_FULL_NAME_LEN} characters`;
    }
    if (companyTrim && companyTrim.length > MAX_COMPANY_NAME_LEN) {
      errors.company_name = `Company name cannot exceed ${MAX_COMPANY_NAME_LEN} characters`;
    }

    if (whatsappDigits) {
      if (!validateWhatsApp(whatsappDigits, whatsappCode)) {
        errors.whatsapp_number = isIndiaDialCode(whatsappCode)
          ? `Enter a valid 10-digit Indian ${showWhatsAppSmsAuth ? 'WhatsApp' : 'mobile'} number starting with 6-9.`
          : `Enter a valid ${showWhatsAppSmsAuth ? 'WhatsApp' : 'mobile'} number (5–15 digits).`;
      }
    } else if (showWhatsAppSmsAuth) {
      errors.whatsapp_number = 'WhatsApp number is required';
    }

    if (showWhatsAppSmsAuth && !whatsappCode) {
      errors.whatsapp_code = 'Country code is required';
    }

    // GST number validation - optional
    if (profile.gst_number && profile.gst_number.trim() && !validateGST(profile.gst_number)) {
      errors.gst_number = 'Enter a valid GST number';
    }

    // Address validation - optional

    if (profile.pincode && !validatePincode(profile.pincode)) errors.pincode = 'Enter a valid 6-digit pincode.';
    // Country, state, and city are now optional - no validation required
    return errors;
  };

  const { merchant, refreshProfile, logout } = useMerchantAuth();

  useEffect(() => {
    loadProfile();
    loadNotificationSettings();
    loadCountries();
    loadBrandLogos();
    loadCountryCodes();
    loadReferralCode();
    checkAuthVisibility();
    getMerchantSubscription()
      .then(r => {
        const status = r.data?.subscription?.status;
        setHasActiveSub(status === 'active' || status === 'grace');
      })
      .catch(() => {});
  }, []);

  const checkAuthVisibility = async () => {
    try {
      const response = await merchantAuthAPI.checkAuthVisibility();
      if (response.data && typeof response.data.show_whatsapp_sms_auth === 'boolean') {
        setShowWhatsAppSmsAuth(response.data.show_whatsapp_sms_auth);
      }
    } catch (error) {
      console.error('❌ MerchantProfile: Failed to check auth visibility:', error);
    }
  };

  // Handle tab from navigation state
  useEffect(() => {
    if (location.state && location.state.activeTab !== undefined) {
      const tabIndex = location.state.activeTab;
      if (tabIndex >= 0 && tabIndex <= 3) {
        setActiveTab(tabIndex);
      }
    }
  }, [location.state]);

  const handleEditProfile = () => {
    if (!canEditProfile) {
      return;
    }
    setEditMode(true);
    setEmailVerification({ otpSent: false, otp: '', verified: true, token: '', loading: false });
    setMobileVerification({ otpSent: false, otp: '', verified: true, token: '', loading: false });
    // Scroll to profile information section on mobile and tablet only
    if (window.innerWidth <= 960 && profileInfoRef.current) { // md breakpoint is 960px
      setTimeout(() => {
        profileInfoRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setFieldErrors({}); // Clear all field validation errors
    setError(''); // Clear any general error messages
    setSuccess(''); // Clear any success messages
    setEmailVerification({ otpSent: false, otp: '', verified: false, token: '', loading: false });
    setMobileVerification({ otpSent: false, otp: '', verified: false, token: '', loading: false });
    loadProfile(); // Reset changes
    // Scroll to top on mobile and tablet only
    if (window.innerWidth <= 960) { // md breakpoint is 960px
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  const handleWhatsappCountryClick = (event) => {
    setWhatsappCountryAnchorEl(event.currentTarget);
    setWhatsappCountrySearch('');
  };

  const handleWhatsappCountryClose = () => {
    setWhatsappCountryAnchorEl(null);
    setWhatsappCountrySearch('');
  };

  const handleWhatsappCountrySelect = (countryCode) => {
    setProfile(prev => ({ ...prev, whatsapp_code: countryCode }));
    const changed =
      (profile.whatsapp_number || '').trim() !== originalContactRef.current.whatsapp_number ||
      (countryCode || '').trim() !== originalContactRef.current.whatsapp_code;
    setMobileVerification({ otpSent: false, otp: '', verified: !changed, token: '', loading: false });
    handleWhatsappCountryClose();
  };

  const filteredWhatsappCountries = countryCodes.filter(c =>
    (c.country_name || '').toLowerCase().includes(whatsappCountrySearch.toLowerCase()) ||
    (c.country_code || '').includes(whatsappCountrySearch)
  );

  useEffect(() => {
    if (whatsappCountryAnchorEl && whatsappSearchInputRef.current) {
      setTimeout(() => {
        whatsappSearchInputRef.current.focus();
      }, 100);
    }
  }, [whatsappCountryAnchorEl]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await merchantAPI.getProfile();
      setProfile(response.data.merchant);
      originalContactRef.current = {
        email: (response.data.merchant.email || '').trim().toLowerCase(),
        whatsapp_number: (response.data.merchant.whatsapp_number || '').trim(),
        whatsapp_code: (response.data.merchant.whatsapp_code || '').trim()
      };
      setEmailVerification({ otpSent: false, otp: '', verified: true, token: '', loading: false });
      setMobileVerification({ otpSent: false, otp: '', verified: true, token: '', loading: false });

      // If country_id is available, load states
      if (response.data.merchant.country_id) {
        loadStates(response.data.merchant.country_id);
      }

      // If state_id is available, load cities
      if (response.data.merchant.state_id) {
        loadCities(response.data.merchant.state_id);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      setError('Failed to load profile information');
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await masterDataConfigAPI.getCountries();
      setCountries(response.data.data || []);
    } catch (error) {
      console.error('Failed to load countries:', error);
      setError('Failed to load countries');
    }
  };

  const loadCountryCodes = async () => {
    try {
      const response = await masterDataConfigAPI.getCountries();
      const codes = response.data.data || [];
      setCountryCodes(codes);
    } catch (error) {
      console.error('Failed to load country codes:', error);
      setError('Failed to load country codes');
    }
  };

  const loadStates = async (countryId) => {
    if (!countryId) return;

    setLoadingStates(true);
    try {
      const response = await masterDataConfigAPI.getStatesByCountry(countryId);
      setStates(response.data.data || []);
    } catch (error) {
      console.error('Failed to load states:', error);
    } finally {
      setLoadingStates(false);
    }
  };

  const loadCities = async (stateId) => {
    if (!stateId) return;

    setLoadingCities(true);
    try {
      const response = await masterDataConfigAPI.getCitiesByState(stateId);
      setCities(response.data.data || []);
    } catch (error) {
      console.error('Failed to load cities:', error);
    } finally {
      setLoadingCities(false);
    }
  };

  const loadNotificationSettings = async () => {
    try {
      const response = await merchantAPI.getNotificationSettings();
      setNotifications(response.data.settings);
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    }
  };

  const loadBrandLogos = async () => {
    try {
      const response = await merchantAPI.getBrandLogos();
      console.log('Brand logos response:', response.data);
      setBrandLogos(response.data.brand_logos || []);
    } catch (error) {
      console.error('Failed to load brand logos:', error);
    }
  };

  const handleAddBrandLogo = () => {
    setEditingBrandLogo(null);
    setBrandLogoData({ brand_name: '', brand_logo_path: '' });
    setBrandDialogError(''); // Clear brand dialog errors
    // Reset background removal options
    setBackgroundRemovalOptions({
      removeBackground: true,  // Default to enabled
      enhanceImage: true,      // Default to enabled
      sensitivity: 1.0
    });
    // Reset preview state
    setSelectedFile(null);
    setShowPreview(false);
    setPreviewData(null);
    setSelectedImageVariant(0);
    // Reset variant progress and status
    setVariantProgress({});
    setVariantStatus({});
    setBrandLogoDialog(true);
  };

  const handleEditBrandLogo = (logo) => {
    setEditingBrandLogo(logo);
    setBrandLogoData({
      brand_name: logo.brand_name,
      brand_logo_path: logo.brand_logo_path || ''
    });
    setBrandDialogError(''); // Clear brand dialog errors
    // Reset background removal options for editing
    setBackgroundRemovalOptions({
      removeBackground: true,  // Default to enabled
      enhanceImage: true,      // Default to enabled
      sensitivity: 1.0
    });
    // Reset preview state
    setSelectedFile(null);
    setShowPreview(false);
    setPreviewData(null);
    setSelectedImageVariant(0);
    // Reset variant progress and status
    setVariantProgress({});
    setVariantStatus({});
    setBrandLogoDialog(true);
  };

  const handleDeleteBrandLogo = (logo) => {
    setBrandLogoToDelete(logo);
    setDeleteBrandLogoDialog(true);
  };

  const confirmDeleteBrandLogo = async () => {
    if (!brandLogoToDelete) return;

    setDeleteBrandLogoLoading(true);
    try {
      await merchantAPI.deleteBrandLogo(brandLogoToDelete.id);
      setSuccess('Brand logo deleted successfully');
      loadBrandLogos();
      setDeleteBrandLogoDialog(false);
      setBrandLogoToDelete(null);
    } catch (error) {
      console.error('Failed to delete brand logo:', error);
      setError('Failed to delete brand logo');
    } finally {
      setDeleteBrandLogoLoading(false);
    }
  };

  const handleBrandLogoSubmit = async () => {
    setBrandDialogError(''); // Clear brand dialog errors

    if (!brandLogoData.brand_name.trim()) {
      setBrandDialogError('Brand name is required');
      return;
    }

    if (!selectedFile) {
      setBrandDialogError('Please select a logo file');
      return;
    }

    setUploadingLogo(true);

    try {
      // First upload the file if we have a selected file
      let logoPath = brandLogoData.brand_logo_path;

      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);

        // Add background removal and enhancement options
        formData.append('remove_background', backgroundRemovalOptions.removeBackground);
        formData.append('enhance_image', backgroundRemovalOptions.enhanceImage);
        formData.append('selected_variant', selectedImageVariant);

        const uploadResponse = await merchantAPI.uploadBrandLogo(formData);
        logoPath = uploadResponse.data.file_path;

        // Show processing information if applied
        if (uploadResponse.data.processing_applied) {
          let processingMessage = 'Logo uploaded successfully';
          if (uploadResponse.data.background_removed) {
            processingMessage += ' with background removed';
          }
          if (uploadResponse.data.image_enhanced) {
            processingMessage += ' and enhanced';
          }
          console.log(processingMessage);
        }
      }

      // Then create or update the brand logo record
      const brandData = {
        ...brandLogoData,
        brand_logo_path: logoPath
      };

      if (editingBrandLogo) {
        await merchantAPI.updateBrandLogo(editingBrandLogo.id, brandData);
        setSuccess('Brand logo updated successfully');
      } else {
        await merchantAPI.createBrandLogo(brandData);
        setSuccess('Brand logo created successfully');
      }

      // Close dialog and reset
      setBrandLogoDialog(false);
      setSelectedFile(null);
      setShowPreview(false);
      setPreviewData(null);
      setBackgroundRemovalOptions({
        removeBackground: true,  // Default to enabled
        enhanceImage: true,      // Default to enabled
        sensitivity: 1.0
      });

      // Refresh the brand logos list
      loadBrandLogos();
    } catch (error) {
      console.error('Failed to save brand logo:', error);
      setBrandDialogError(error.response?.data?.error || 'Failed to save brand logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoUploadClick = async (event) => {
    // Prevent event bubbling
    if (event) {
      event.stopPropagation();
    }

    // Check if already processing to prevent multiple calls
    if (isCheckingPermissions) {
      console.log('⏳ Permission check already in progress, skipping...');
      return;
    }

    // Check Flutter permissions on mobile before opening file dialog
    if (FlutterServicesAPI.isFlutterApp()) {
      try {
        setIsCheckingPermissions(true);
        console.log('📱 Checking file permissions on mobile (logo upload)...');
        const permissionResult = await FlutterServicesAPI.checkFilePermissions();
        console.log('🔐 Permission result:', permissionResult);

        if (!permissionResult.can_upload) {
          console.log('❌ Logo upload blocked - no permissions');
          setError('File upload permission denied. Please check your device permissions.');
          return;
        }

        console.log('✅ Logo upload permission granted, opening file dialog');
      } catch (error) {
        console.error('❌ Error checking file permissions:', error);
        setError('Failed to check file permissions. Please try again.');
        return;
      } finally {
        setIsCheckingPermissions(false);
      }
    }

    // Open file dialog if permissions are granted or not in Flutter app
    const fileInput = document.getElementById('logo-upload');
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setBrandDialogError(''); // Clear brand dialog errors

    // File type validation
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    if (!allowedTypes.includes(file.type)) {
      setBrandDialogError('Please select only JPG, JPEG, PNG, WebP, or HEIC files.');
      return;
    }

    // File size validation (6MB = 6 * 1024 * 1024 bytes)
    const maxSize = 6 * 1024 * 1024; // 6MB in bytes
    if (file.size > maxSize) {
      setBrandDialogError('File size must be less than 6MB.');
      return;
    }

    setSelectedFile(file);
    setShowPreview(false);
    setPreviewData(null);

    // Automatically trigger preview
    await handlePreview(file);
  };

  const handlePreview = async (file = null) => {
    const fileToProcess = file || selectedFile;
    if (!fileToProcess) return;

    setPreviewLoading(true);
    setBrandDialogError('');

    // Reset variant progress and status
    setVariantProgress({});
    setVariantStatus({});

    try {
      const formData = new FormData();
      formData.append('file', fileToProcess);

      // Add background removal and enhancement options (default to true)
      formData.append('remove_background', true);
      formData.append('enhance_image', true);

      // Simulate individual variant progress
      const simulateVariantProgress = () => {
        const variants = ['original', 'variant1', 'variant2', 'variant3'];

        variants.forEach((variant, index) => {
          // Set initial loading state
          setVariantStatus(prev => ({ ...prev, [variant]: 'loading' }));
          setVariantProgress(prev => ({ ...prev, [variant]: 0 }));

          // Simulate progress for each variant
          const progressInterval = setInterval(() => {
            setVariantProgress(prev => {
              const currentProgress = prev[variant] || 0;
              const newProgress = Math.min(currentProgress + Math.random() * 20, 100);

              if (newProgress >= 100) {
                clearInterval(progressInterval);
                setVariantStatus(prevStatus => ({ ...prevStatus, [variant]: 'completed' }));
              }

              return { ...prev, [variant]: newProgress };
            });
          }, 200 + (index * 100)); // Stagger the progress updates
        });
      };

      // Start progress simulation
      simulateVariantProgress();

      const response = await merchantAPI.previewBrandLogo(formData);
      setPreviewData(response.data);
      setShowPreview(true);

      // Mark all variants as completed when API response is received
      setTimeout(() => {
        const variants = ['original', 'variant1', 'variant2', 'variant3'];
        variants.forEach(variant => {
          setVariantStatus(prev => ({ ...prev, [variant]: 'completed' }));
          setVariantProgress(prev => ({ ...prev, [variant]: 100 }));
        });
      }, 1000);

    } catch (error) {
      console.error('Failed to preview logo:', error);
      setBrandDialogError(error.response?.data?.error || 'Failed to preview logo');

      // Mark all variants as error
      const variants = ['original', 'variant1', 'variant2', 'variant3'];
      variants.forEach(variant => {
        setVariantStatus(prev => ({ ...prev, [variant]: 'error' }));
      });
    } finally {
      setPreviewLoading(false);
    }
  };



  const sendEmailVerificationOtp = async () => {
    try {
      setEmailVerification((prev) => ({ ...prev, loading: true }));
      await merchantAuthAPI.sendProfileEmailOtp((profile.email || '').trim().toLowerCase());
      setEmailVerification((prev) => ({ ...prev, otpSent: true, verified: false, token: '' }));
      setSuccess('OTP sent to email');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to send email OTP');
    } finally {
      setEmailVerification((prev) => ({ ...prev, loading: false }));
    }
  };

  const verifyEmailOtp = async () => {
    try {
      setEmailVerification((prev) => ({ ...prev, loading: true }));
      const otp = normalizeOtpInput(emailVerification.otp);
      const response = await merchantAuthAPI.verifyProfileEmailOtp(
        (profile.email || '').trim().toLowerCase(),
        otp
      );
      setEmailVerification({
        otpSent: true,
        otp: '',
        verified: true,
        token: response.data.verification_token || '',
        loading: false
      });
      setFieldErrors((prev) => ({ ...prev, email: '' }));
      setSuccess('Email verified successfully');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to verify email OTP');
      setEmailVerification((prev) => ({ ...prev, loading: false }));
    }
  };

  const sendMobileVerificationOtp = async () => {
    try {
      setMobileVerification((prev) => ({ ...prev, loading: true }));
      await merchantAuthAPI.sendProfileMobileOtp(
        (profile.whatsapp_number || '').trim(),
        (profile.whatsapp_code || '+91').trim()
      );
      setMobileVerification((prev) => ({ ...prev, otpSent: true, verified: false, token: '' }));
      setSuccess('OTP sent to mobile');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to send mobile OTP');
    } finally {
      setMobileVerification((prev) => ({ ...prev, loading: false }));
    }
  };

  const verifyMobileOtp = async () => {
    try {
      setMobileVerification((prev) => ({ ...prev, loading: true }));
      const otp = normalizeOtpInput(mobileVerification.otp);
      const response = await merchantAuthAPI.verifyProfileMobileOtp(
        (profile.whatsapp_number || '').trim(),
        (profile.whatsapp_code || '+91').trim(),
        otp
      );
      setMobileVerification({
        otpSent: true,
        otp: '',
        verified: true,
        token: response.data.verification_token || '',
        loading: false
      });
      setFieldErrors((prev) => ({ ...prev, whatsapp_number: '' }));
      setSuccess('Mobile verified successfully');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to verify mobile OTP');
      setMobileVerification((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    const errors = validateFields(profile);
    const currentEmail = (profile.email || '').trim().toLowerCase();
    const originalEmail = originalContactRef.current.email;
    const currentWhatsapp = (profile.whatsapp_number || '').trim();
    const currentWhatsappCode = (profile.whatsapp_code || '').trim();
    const originalWhatsapp = originalContactRef.current.whatsapp_number;
    const originalWhatsappCode = originalContactRef.current.whatsapp_code;
    const emailChanged = currentEmail !== originalEmail;
    const mobileChanged = currentWhatsapp !== originalWhatsapp || currentWhatsappCode !== originalWhatsappCode;

    if (emailChanged && !emailVerification.verified) {
      errors.email = 'Please verify updated email before saving.';
    }
    if (mobileChanged && !mobileVerification.verified) {
      errors.whatsapp_number = 'Please verify updated mobile number before saving.';
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setLoading(false);
      return;
    }
    try {
      // Same default as send/verify OTP so JWT mobile token matches PUT payload.
      const effectiveWhatsappCode = profile.whatsapp_number
        ? (profile.whatsapp_code || '+91').trim()
        : '';
      const normalizedWhatsapp = normalizeLocalPhoneDigits(profile.whatsapp_number);
      const trimmedName = String(profile.name || '').trim();
      const trimmedCompany = String(profile.company_name || '').trim();
      const updateData = {
        name: trimmedName,
        email: (profile.email || '').trim().toLowerCase(),
        whatsapp_number: normalizedWhatsapp,
        whatsapp_code: effectiveWhatsappCode,
        company_name: trimmedCompany,
        gst_number: profile.gst_number ? profile.gst_number.toUpperCase() : '',
        address: profile.address,
        country_id: profile.country_id,
        state_id: profile.state_id,
        city_id: profile.city_id,
        pincode: profile.pincode,
        email_verification_token: emailChanged ? emailVerification.token : null,
        mobile_verification_token: mobileChanged ? mobileVerification.token : null
      };

      await merchantAPI.updateProfile(updateData);

      await loadProfile();
      setEditMode(false);
      setSuccess('Profile updated successfully!');
      refreshProfile?.();
    } catch (error) {
      console.error('Failed to update profile:', error);
      console.error('Error response:', error.response);
      setError(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationUpdate = async (settingType, value) => {
    try {
      const updatedSettings = {
        ...notifications,
        [settingType]: value
      };

      await merchantAPI.updateNotificationSettings(updatedSettings);
      setNotifications(updatedSettings);
      setSuccess('Notification settings updated!');
    } catch (error) {
      console.error('Failed to update notification settings:', error);
      setError('Failed to update notification settings');
    }
  };

  const handlePasswordInputChange = (field, value) => {
    setPasswordData({ ...passwordData, [field]: value });
    // Clear error when user starts typing
    if (passwordError) {
      setPasswordError('');
    }
  };

  const handlePasswordChange = async () => {
    // Clear any previous errors
    setPasswordError('');

    // Validate current password is not empty
    if (!passwordData.current_password.trim()) {
      setPasswordError('Current password is required');
      return;
    }

    // Validate new password is not empty
    if (!passwordData.new_password.trim()) {
      setPasswordError('New password is required');
      return;
    }

    // Validate new password is different from current password
    if (passwordData.current_password === passwordData.new_password) {
      setPasswordError('New password must be different from current password');
      return;
    }

    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    // Validate password length
    if (passwordData.new_password.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    try {
      await merchantAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });

      // Reset form fields
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      // Reset password visibility states
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setPasswordError('');
      setShowPasswordForm(false); // Hide the form on successful password change
      setSuccess('Password changed successfully!');

      // Refresh profile data to get updated password_updated_at timestamp
      await loadProfile();
    } catch (error) {
      console.error('Failed to change password:', error);
      setPasswordError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (e) => {
    const countryId = e.target.value;

    // Update profile with new country_id and reset state_id and city_id
    setProfile({
      ...profile,
      country_id: countryId,
      state_id: '',
      city_id: ''
    });

    // Clear states and cities
    setStates([]);
    setCities([]);

    // Load states for the selected country
    loadStates(countryId);

    // Clear any field errors
    setFieldErrors({
      ...fieldErrors,
      country_id: '',
      state_id: '',
      city_id: ''
    });
  };

  const handleStateChange = (e) => {
    const stateId = e.target.value;

    // Update profile with new state_id and reset city_id
    setProfile({
      ...profile,
      state_id: stateId,
      city_id: ''
    });

    // Clear cities
    setCities([]);

    // Load cities for the selected state
    loadCities(stateId);

    // Clear any field errors
    setFieldErrors({
      ...fieldErrors,
      state_id: '',
      city_id: ''
    });
  };

  const handleCityChange = (e) => {
    const cityId = e.target.value;

    // Update profile with new city_id
    setProfile({
      ...profile,
      city_id: cityId
    });

    // Clear any field errors
    setFieldErrors({
      ...fieldErrors,
      city_id: ''
    });
  };

  // Load merchant's referral code from the API
  const loadReferralCode = async () => {
    setReferralCodeLoading(true);
    try {
      const response = await referralCodeAPI.getMyReferralCode();
      if (response.data.has_assigned_code) {
        setReferralCode(response.data.assigned_code);
      }
    } catch (error) {
      console.error('Failed to load referral code:', error);
      // Don't show error for referral code loading failure - referral codes are optional
    } finally {
      setReferralCodeLoading(false);
    }
  };

  // Handle copying referral code to clipboard with fallback for older browsers
  const handleCopyReferralCode = async (code) => {
    try {
      // Try modern clipboard API first
      await navigator.clipboard.writeText(code);
      setCopySuccess(true);
      // Since we now only copy the full message, always show this message
      setCopyMessage('Full referral message copied to clipboard!');
      setTimeout(() => {
        setCopySuccess(false);
        setCopyMessage('');
      }, 2000);
    } catch (error) {
      console.error('Failed to copy referral code:', error);
      // Fallback for older browsers using document.execCommand
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      // Since we now only copy the full message, always show this message
      setCopyMessage('Full referral message copied to clipboard!');
      setTimeout(() => {
        setCopySuccess(false);
        setCopyMessage('');
      }, 2000);
    }
  };

  // Handle referral code sharing - triggers Flutter handler on mobile, shows dialog on web
  const handleReferralCodeShare = async () => {
    if (!referralCode) {
      setError('No referral code available to share');
      return;
    }

    // Check if running in Flutter app
    if (FlutterServicesAPI.isFlutterApp()) {
      try {
        console.log('📱 Flutter app detected, triggering socialShare handler...');

        // Prepare share data with referral code in URL
        const referralUrl = getRegisterUrl(referralCode);

        const shareData = {
          referralCode: referralCode,
          shareMessage: `Hi! I'm using Glammify to create professional model photos of my jewellery instantly. You should try it too 👉 ${referralUrl}\nUse my code ${referralCode} for bonus ${FlutterServicesAPI.getCreditLabel().toLowerCase()}!`,
          shareUrl: referralUrl,
          title: 'Share Glammify Referral Code'
        };

        console.log('📱 Share data prepared:', shareData);

        // Call Flutter handler for social sharing
        const result = await window.flutter_inappwebview.callHandler('socialShare', shareData);

        console.log('📱 Flutter socialShare handler response:', result);

        if (result && result.success) {
          setSuccess('Referral code shared successfully!');
        } else {
          console.warn('⚠️ Flutter socialShare handler returned unsuccessful result:', result);
          // Don't show web dialog in Flutter app - just show error

        }

      } catch (error) {
        console.error('❌ Error calling Flutter socialShare handler:', error);
        // Don't show web dialog in Flutter app - just show error

      }
    } else {
      // Web environment - show share dialog
      console.log('🌐 Web environment detected, showing share dialog...');
      setShareDialog(true);
    }
  };

  // Handle delete account
  const handleDeleteAccount = async () => {
    try {
      setDeleteAccountLoading(true);
      await merchantAPI.deleteAccount();
      setSuccess('Account deleted successfully');
      setDeleteAccountDialog(false);

      // Logout the user after successful account deletion
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);

    } catch (error) {
      console.error('Error deleting account:', error);
      setError(error.response?.data?.error || 'Failed to delete account');
    } finally {
      setDeleteAccountLoading(false);
    }
  };

  const renderProfileTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card elevation={0} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                mx: 'auto',
                mb: 2,
                background: '#6318C6',
                color: 'white',
                fontSize: '2rem',
                boxShadow: '0 4px 20px rgba(147, 51, 238, 0.3)',
                border: '3px solid white'
              }}
            >
              {profile.name?.charAt(0)?.toUpperCase()}
            </Avatar>

            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              {profile.name}
            </Typography>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              {profile.email}
            </Typography>

            <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={2}>
              {profile.email_verified ? (
                <Chip
                  icon={<Verified />}
                  label="Verified"
                  color="success"
                  size="small"
                />
              ) : (
                <Chip
                  icon={<Warning />}
                  label="Not Verified"
                  color="warning"
                  size="small"
                />
              )}
            </Box>

            {/* Referral Code Section - Professional design with single copy button */}
            {referralCodeLoading ? (
              <Box
                sx={{
                  mb: 2,
                  p: 3,
                  bgcolor: '#f8f9fa',
                  borderRadius: 3,
                  border: '1px solid #e9ecef',
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                <CircularProgress size={24} sx={{ color: '#6318C6', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Loading referral code...
                </Typography>
              </Box>
            ) : referralCode ? (
              <Box
                sx={{
                  mb: 2,
                  p: 2.5,
                  background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
                  borderRadius: 3,
                  border: '1px solid #e5e7eb',
                  textAlign: 'center',
                  boxShadow: '0 3px 16px rgba(0,0,0,0.06)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)'
                  }
                }}
              >
                {/* Referral Code Badge */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2,
                    py: 0.75,
                    mb: 2,
                    bgcolor: 'rgba(99, 24, 198, 0.08)',
                    borderRadius: 2.5,
                    border: '1px solid rgba(99, 24, 198, 0.15)'
                  }}
                >
                  <Typography
                    variant="caption"
                    color="#6318C6"
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                      fontSize: '10px'
                    }}
                  >
                    Referral Code
                  </Typography>
                </Box>

                {/* Referral Code Display with Share Icon */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2.5,
                    mb: 2,
                    p: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: 2.5,
                    border: '1px solid rgba(99, 24, 198, 0.1)',
                    boxShadow: '0 2px 8px rgba(99, 24, 198, 0.08)'
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: '800',
                      color: '#4C1D95',
                      letterSpacing: '0.15em',
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      textShadow: '0 1px 2px rgba(99, 24, 198, 0.15)'
                    }}
                  >
                    {referralCode}
                  </Typography>

                  {/* Share Icon */}
                  <IconButton
                    onClick={() => handleReferralCodeShare()}
                    sx={{
                      color: '#4C1D95',
                      backgroundColor: 'rgba(99, 24, 198, 0.06)',
                      border: '1.5px solid rgba(99, 24, 198, 0.12)',
                      width: 38,
                      height: 38,
                      '&:hover': {
                        backgroundColor: 'rgba(99, 24, 198, 0.12)',
                        borderColor: 'rgba(99, 24, 198, 0.2)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 3px 12px rgba(99, 24, 198, 0.2)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                    title="Share referral code"
                  >
                    <Share sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  mb: 2,
                  p: 2.5,
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                  borderRadius: 3,
                  border: '2px dashed #d1d5db',
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                  Referral Code
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.7, fontSize: '13px' }}>
                  No referral code assigned yet
                </Typography>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary">
              Member since {new Date(profile.created_at).toLocaleDateString('en-GB')}
            </Typography>
            {canEditProfile && (
              <Button
                variant="outlined"
                startIcon={<Edit />}
                fullWidth
                sx={{ mt: 2, borderColor: '#6318C6', color: '#6318C6', '&:hover': { borderColor: '#6318C6', backgroundColor: '#f3e8ff', color: '#6318C6' } }}
                onClick={handleEditProfile}
                disabled={editMode}
              >
                Edit Profile
              </Button>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card elevation={0} sx={{
          boxShadow: 'none',
          border: { xs: 'none', sm: 'none', md: '1px solid #e5e7eb' },
          bgcolor: { xs: 'transparent', sm: 'transparent', md: 'white' },
          borderRadius: '8px'
        }}>
          <CardContent sx={{
            padding: { xs: 0, sm: 0, md: '16px' },
            '&:last-child': { paddingBottom: { xs: 0, sm: 0, md: '16px' } },
            bgcolor: { xs: 'transparent', sm: 'transparent', md: 'white' },
            // Global overrides for all inputs and selects within this component
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#6318C6 !important',
              borderWidth: '2px !important'
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#6318C6 !important'
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#6318C6'
            },
            '& .MuiSelect-select.Mui-focused': {
              color: '#6318C6'
            },
            '& .MuiSelect-icon.Mui-focused': {
              color: '#6318C6'
            }
          }}>
            <Box
              ref={profileInfoRef}
              mb={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" fontSize={{ xs: "18px", sm: "20px", md: "27px" }}>
                Profile Information
              </Typography>

              {editMode && (
                <Box
                  display={{ xs: 'none', sm: 'none', md: 'flex' }}
                  gap={1}
                >
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleCancel}
                    sx={{
                      borderColor: 'black',
                      color: 'black',
                      '&:hover': { borderColor: 'black', backgroundColor: '#f3f4f6', color: 'black' }
                    }}
                  >
                    CANCEL
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleProfileUpdate}
                    disabled={loading}
                    sx={{
                      background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
                      color: 'white',
                      boxShadow: 'none',
                      '&:hover': {
                        background: '#6318C6',
                        boxShadow: '0 2px 8px 0 rgba(147,51,238,0.15)'
                      }
                    }}
                  >
                    Save Changes
                  </Button>
                </Box>
              )}
            </Box>

            {loading && <LinearProgress sx={{ mb: 2 }} />}

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Full Name *"
                  value={profile.name || ''}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  disabled={!editMode}
                  error={!!fieldErrors.name}
                  helperText={fieldErrors.name}
                  inputProps={{ maxLength: MAX_FULL_NAME_LEN }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '14px', sm: '14px', md: '16px' }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '14px', sm: '14px', md: '16px' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Email *"
                  type="email"
                  value={profile.email || ''}
                  onChange={(e) => {
                    const email = e.target.value;
                    setProfile({ ...profile, email });
                    setFieldErrors({ ...fieldErrors, email: '' });
                    const changed = email.trim().toLowerCase() !== originalContactRef.current.email;
                    setEmailVerification({ otpSent: false, otp: '', verified: !changed, token: '', loading: false });
                  }}
                  disabled={!editMode}
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '14px', sm: '14px', md: '16px' },
                      ...(!editMode ? { color: 'text.secondary' } : {}),
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '14px', sm: '14px', md: '16px' },
                    },
                    ...(!editMode
                      ? {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#f5f5f5',
                          borderColor: '#d1d5db',
                        },
                      }
                      : {}),
                  }}
                />
                {editMode && (profile.email || '').trim().toLowerCase() !== originalContactRef.current.email && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      type="button"
                      onClick={sendEmailVerificationOtp}
                      disabled={emailVerification.loading || emailVerification.verified}
                    >
                      {emailVerification.verified ? 'Verified' : 'Verify Email'}
                    </Button>
                    {emailVerification.otpSent && !emailVerification.verified && (
                      <>
                        <TextField
                          size="small"
                          placeholder="Enter OTP"
                          value={emailVerification.otp}
                          onChange={(e) => setEmailVerification((prev) => ({ ...prev, otp: e.target.value }))}
                          sx={{ width: 140 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          type="button"
                          sx={{
                            flex: 1,
                            maxWidth: '160px',
                            borderRadius: '10px',
                            backgroundColor: '#651BC6',
                            color: '#fff',
                            border: '1px solid #651BC6',
                            height: '36px',
                            fontWeight: 500,
                            textTransform: 'none',
                            fontSize: '14px',
                            fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                            boxShadow: 'none',
                            '&:hover': {
                              backgroundColor: '#5015a1',
                              border: '1px solid #5015a1',
                              boxShadow: 'none',
                            },
                            '&:disabled': {
                              backgroundColor: '#d1d5db',
                              borderColor: '#d1d5db',
                              color: '#9ca3af',
                            },
                          }}
                          onClick={verifyEmailOtp}
                          disabled={emailVerification.loading || !normalizeOtpInput(emailVerification.otp)}
                        >
                          Verify OTP
                        </Button>
                      </>
                    )}
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Box display="flex" gap={1}>
                  <FormControl sx={{ minWidth: { xs: 80, sm: 80, md: 100 } }}>
                    <Typography variant="caption" sx={{ mb: 0.5, color: 'text.secondary', ml: 1 }}>
                      Code
                    </Typography>
                    <Box
                      sx={{
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        px: '12px',
                        border: '1px solid #E0E0E0',
                        borderRadius: '4px',
                        backgroundColor: '#f5f5f5',
                        cursor: 'default',
                        fontSize: '14px',
                        color: '#1A1A1A',
                        justifyContent: 'space-between',
                        pointerEvents: 'none',
                      }}
                    >
                      <span>{profile.whatsapp_code || '+91'}</span>
                    </Box>

                    {/* Country Code Popover */}
                    <Popover
                      open={Boolean(whatsappCountryAnchorEl)}
                      anchorEl={whatsappCountryAnchorEl}
                      onClose={handleWhatsappCountryClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      PaperProps={{
                        sx: {
                          width: '300px',
                          maxHeight: '350px',
                          borderRadius: '12px',
                          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                          mt: 0.5,
                        }
                      }}
                    >
                      {/* Search field */}
                      <Box sx={{ p: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                        <TextField
                          inputRef={whatsappSearchInputRef}
                          fullWidth
                          size="small"
                          placeholder="Search country..."
                          value={whatsappCountrySearch}
                          onChange={(e) => setWhatsappCountrySearch(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              fontSize: '13px',
                            }
                          }}
                        />
                      </Box>
                      {/* Country list */}
                      <List sx={{ p: 0, overflow: 'auto', maxHeight: '280px' }}>
                        {filteredWhatsappCountries.map((country) => (
                          <ListItemButton
                            key={country.id}
                            onClick={() => handleWhatsappCountrySelect(country.country_code)}
                            selected={country.country_code === profile.whatsapp_code}
                            sx={{
                              py: 0.8,
                              px: 2,
                              '&.Mui-selected': {
                                backgroundColor: '#f3e8ff',
                              },
                              '&:hover': {
                                backgroundColor: '#f9f5ff',
                              },
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography sx={{ fontSize: '13px', color: '#1A1A1A' }}>
                                  <span style={{ display: 'inline-block', width: '45px', textAlign: 'left' }}>{country.country_code}</span>
                                  {country.country_name}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        ))}
                        {filteredWhatsappCountries.length === 0 && (
                          <Box sx={{ py: 2, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '13px', color: '#9e9e9e' }}>
                              No countries found
                            </Typography>
                          </Box>
                        )}
                      </List>
                    </Popover>
                    {fieldErrors.whatsapp_code && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {fieldErrors.whatsapp_code}
                      </Typography>
                    )}
                  </FormControl>
                  <Tooltip
                    title="Mobile number is read-only after registration. Contact support@glammify.ai to update."
                    placement="top"
                    arrow
                  >
                    <TextField
                      fullWidth
                      required={showWhatsAppSmsAuth}
                      label={showWhatsAppSmsAuth ? "WhatsApp Number *" : "Mobile Number"}
                      type="tel"
                      inputMode="tel"
                      value={profile.whatsapp_number || ''}
                      onChange={(e) => {
                        const whatsapp_number = e.target.value;
                        setProfile({ ...profile, whatsapp_number });
                        setFieldErrors({ ...fieldErrors, whatsapp_number: '' });
                        const changed =
                          whatsapp_number.trim() !== originalContactRef.current.whatsapp_number ||
                          (profile.whatsapp_code || '').trim() !== originalContactRef.current.whatsapp_code;
                        setMobileVerification({ otpSent: false, otp: '', verified: !changed, token: '', loading: false });
                      }}
                      disabled
                      error={!!fieldErrors.whatsapp_number}
                      helperText={fieldErrors.whatsapp_number}
                      inputProps={{ maxLength: 20, inputMode: 'numeric' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <LockOutlined sx={{ fontSize: 16, color: '#9ca3af' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '14px', sm: '14px', md: '16px' },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '14px', sm: '14px', md: '16px' },
                        },
                      }}
                    />
                  </Tooltip>
                  <Typography sx={{ fontSize: 11, color: '#6b7280', mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LockOutlined sx={{ fontSize: 12 }} />
                    Read-only after registration
                  </Typography>
                </Box>
                {editMode &&
                  (((profile.whatsapp_number || '').trim() !== originalContactRef.current.whatsapp_number) ||
                    ((profile.whatsapp_code || '').trim() !== originalContactRef.current.whatsapp_code)) && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        type="button"
                        onClick={sendMobileVerificationOtp}
                        disabled={mobileVerification.loading || mobileVerification.verified}
                      >
                        {mobileVerification.verified ? 'Verified' : 'Verify Number'}
                      </Button>
                      {mobileVerification.otpSent && !mobileVerification.verified && (
                        <>
                          <TextField
                            size="small"
                            placeholder="Enter OTP"
                            value={mobileVerification.otp}
                            onChange={(e) => setMobileVerification((prev) => ({ ...prev, otp: e.target.value }))}
                            sx={{ width: 140 }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            type="button"
                            sx={{
                              flex: 1,
                              maxWidth: '160px',
                              borderRadius: '10px',
                              backgroundColor: '#651BC6',
                              color: '#fff',
                              border: '1px solid #651BC6',
                              height: '36px',
                              fontWeight: 500,
                              textTransform: 'none',
                              fontSize: '14px',
                              fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                              boxShadow: 'none',
                              '&:hover': {
                                backgroundColor: '#5015a1',
                                border: '1px solid #5015a1',
                                boxShadow: 'none',
                              },
                              '&:disabled': {
                                backgroundColor: '#d1d5db',
                                borderColor: '#d1d5db',
                                color: '#9ca3af',
                              },
                            }}
                            onClick={verifyMobileOtp}
                            disabled={mobileVerification.loading || !normalizeOtpInput(mobileVerification.otp)}
                          >
                            Verify OTP
                          </Button>
                        </>
                      )}
                    </Box>
                  )}
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Company Name *"
                  value={profile.company_name || ''}
                  onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                  disabled={!editMode}
                  error={!!fieldErrors.company_name}
                  helperText={fieldErrors.company_name}
                  inputProps={{ maxLength: MAX_COMPANY_NAME_LEN }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '14px', sm: '14px', md: '16px' }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '14px', sm: '14px', md: '16px' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GST Number"
                  value={profile.gst_number || ''}
                  onChange={(e) => {
                    setProfile({ ...profile, gst_number: e.target.value });
                    setFieldErrors({ ...fieldErrors, gst_number: '' });
                  }}
                  disabled={!editMode}
                  placeholder="e.g. 27AAPFU0939F1ZV"
                  error={!!fieldErrors.gst_number}
                  helperText={fieldErrors.gst_number || "Enter your 15-digit GSTIN (e.g. 27AAPFU0939F1ZV)"}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '14px', sm: '14px', md: '16px' }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '14px', sm: '14px', md: '16px' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={profile.address || ''}
                  onChange={(e) => {
                    setProfile({ ...profile, address: e.target.value });
                    setFieldErrors({ ...fieldErrors, address: '' });
                  }}
                  disabled={!editMode}
                  error={!!fieldErrors.address}
                  helperText={fieldErrors.address}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '14px', sm: '14px', md: '16px' }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '14px', sm: '14px', md: '16px' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Tooltip
                  title="Country is read-only after registration. Contact support@glammify.ai to update."
                  placement="top"
                  arrow
                >
                  <FormControl fullWidth error={!!fieldErrors.country_id} disabled>
                    <InputLabel id="country-label">
                      Country
                      <LockOutlined sx={{ fontSize: 13, ml: 0.5, verticalAlign: 'middle', color: '#9ca3af' }} />
                    </InputLabel>
                    <Select
                      labelId="country-label"
                      id="country-select"
                      value={profile.country_id || ''}
                      label="Country"
                      onChange={handleCountryChange}
                    >
                      <MenuItem value="">
                        <em>Select Country</em>
                      </MenuItem>
                      {countries.map((country) => (
                        <MenuItem key={country.id} value={country.id}>
                          {country.country_name}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.country_id && (
                      <Typography variant="caption" color="error">
                        {fieldErrors.country_id}
                      </Typography>
                    )}
                  </FormControl>
                </Tooltip>
                <Typography sx={{ fontSize: 11, color: '#6b7280', mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LockOutlined sx={{ fontSize: 12 }} />
                  Read-only after registration
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth error={!!fieldErrors.state_id} disabled={!editMode || loadingStates}>
                  <InputLabel id="state-label">State</InputLabel>
                  <Select
                    labelId="state-label"
                    id="state-select"
                    value={profile.state_id || ''}
                    label="State"
                    onChange={handleStateChange}
                  >
                    <MenuItem value="">
                      <em>{loadingStates ? 'Loading...' : 'Select State'}</em>
                    </MenuItem>
                    {states.map((state) => (
                      <MenuItem key={state.id} value={state.id}>
                        {state.state_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.state_id && (
                    <Typography variant="caption" color="error">
                      {fieldErrors.state_id}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl
                  fullWidth
                  error={!!fieldErrors.city_id}
                  disabled={!editMode || loadingCities}
                  className={fieldErrors.city_id ? 'city-input-error' : ''}
                >
                  <InputLabel id="city-label">City</InputLabel>
                  <Select
                    labelId="city-label"
                    id="city-select"
                    value={profile.city_id || ''}
                    label="City"
                    onChange={handleCityChange}
                  >
                    <MenuItem value="">
                      <em>{loadingCities ? 'Loading...' : 'Select City'}</em>
                    </MenuItem>
                    {cities.map((city) => (
                      <MenuItem key={city.id} value={city.id}>
                        {city.city_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.city_id && (
                    <Typography variant="caption" color="error">
                      {fieldErrors.city_id}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pincode"
                  value={profile.pincode || ''}
                  onChange={(e) => {
                    setProfile({ ...profile, pincode: e.target.value });
                    setFieldErrors({ ...fieldErrors, pincode: '' });
                  }}
                  disabled={!editMode}
                  error={!!fieldErrors.pincode}
                  helperText={fieldErrors.pincode}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '14px', sm: '14px', md: '16px' }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '14px', sm: '14px', md: '16px' }
                    }
                  }}
                />
              </Grid>
            </Grid>

            {editMode && (
              <Box
                mt={3}
                display={{ xs: 'flex', sm: 'flex', md: 'none' }}
                flexDirection="column"
                gap={1}
                width="100%"
              >
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleProfileUpdate}
                  disabled={loading}
                  sx={{
                    background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
                    color: 'white',
                    boxShadow: 'none',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
                      boxShadow: '0 2px 8px 0 rgba(147,51,238,0.15)'
                    },
                    width: '100%',
                    height: '45px'
                  }}
                >
                  Save Changes
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancel}
                  sx={{
                    borderColor: 'black',
                    color: 'black',
                    '&:hover': { borderColor: 'black', backgroundColor: '#f3f4f6', color: 'black' },
                    width: '100%',
                    height: '45px'
                  }}
                >
                  CANCEL
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderBrandLogoTab = () => (
    <Card elevation={0} sx={{ boxShadow: 'none', border: { xs: 'none', sm: 'none', md: '1px solid #e5e7eb' }, borderRadius: '8px' }}>
      <CardContent sx={{
        padding: { xs: '16px', sm: '16px', md: '16px' },
        '&:last-child': { paddingBottom: { xs: '16px', sm: '16px', md: '16px' } }
      }}>
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontSize={{ xs: "18px", sm: "20px", md: "27px" }}>
              Brands
            </Typography>
            {canCreateBrand && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddBrandLogo}
                sx={{
                  backgroundColor: '#6318C6',
                  color: 'white',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#7c3aed',
                    boxShadow: '0 2px 8px 0 rgba(147,51,238,0.15)'
                  },
                  height: { xs: '40px', sm: '40px', md: '48px' },
                  px: { xs: 2, sm: 2, md: 3 }
                }}
              >
                Add Brand
              </Button>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Manage your brands for AI generation.
          </Typography>
        </Box>

        {brandLogos.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Image sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Brands
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              You haven't added any brands yet. Add your first brand to get started.
            </Typography>
            {canCreateBrand && (
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddBrandLogo}
                sx={{
                  borderColor: '#6318C6',
                  color: '#6318C6',
                  '&:hover': { borderColor: '#6318C6', backgroundColor: '#f3e8ff' }
                }}
              >
                Add Your First Brand
              </Button>
            )}
          </Box>
        ) : (
          <Grid container spacing={2}>
            {brandLogos.map((logo) => (
              <Grid item xs={12} sm={6} md={4} key={logo.id}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    '&:hover': { borderColor: '#6318C6', boxShadow: '0 2px 8px 0 rgba(147,51,238,0.1)' }
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="h6" fontWeight={600}>
                        {logo.brand_name}
                      </Typography>

                      <Box display="flex" gap={1}>
                        {canEditBrand && (
                          <IconButton
                            size="small"
                            onClick={() => handleEditBrandLogo(logo)}
                            sx={{ color: '#6318C6' }}
                          >
                            <Edit />
                          </IconButton>
                        )}
                        {canDeleteBrand && (
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteBrandLogo(logo)}
                            sx={{ color: '#ef4444' }}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    {logo.brand_logo_path ? (
                      <Box
                        sx={{
                          width: '100%',
                          height: 120,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid #e5e7eb',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#f9fafb'
                        }}
                      >
                        <SecureImage
                          src={getImageUrl(logo.brand_logo_path)}
                          alt={logo.brand_name}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            console.error('Failed to load image:', e.target.src);
                          }}
                          onLoad={() => {
                            console.log('Image loaded successfully:', getImageUrl(logo.brand_logo_path));
                          }}
                        />
                        <Box
                          display="none"
                          alignItems="center"
                          justifyContent="center"
                          sx={{ color: 'text.secondary' }}
                        >
                          <Image sx={{ fontSize: 40 }} />
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: 120,
                          borderRadius: 1,
                          border: '1px dashed #d1d5db',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#f9fafb',
                          color: 'text.secondary'
                        }}
                      >
                        <Typography variant="body2">No Logo Uploaded</Typography>
                      </Box>
                    )}

                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                      Created: {new Date(logo.created_at).toLocaleDateString('en-GB')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );

  const renderNotificationsTab = () => {
    // Define notification types with their details
    const notificationTypes = [
      {
        key: 'generation_complete',
        icon: <Notifications />,
        title: 'Generation Complete',
        description: 'Notify when AI generation is completed'
      },
      {
        key: 'low_credits_alert',
        icon: <Warning />,
        title: `Low ${FlutterServicesAPI.getCreditLabel()} Alert`,
        description: `Notify when ${FlutterServicesAPI.getCreditLabel().toLowerCase()} balance is low`
      },
      {
        key: 'refund_notifications',
        icon: <AccountBalanceWallet />,
        title: 'Refund Notifications',
        description: 'Notify when refunds are processed'
      },
      {
        key: 'credit_request_notifications',
        icon: <Storefront />,
        title: `${FlutterServicesAPI.getCreditLabel()} Request Notifications`,
        description: `Notify when ${FlutterServicesAPI.getCreditLabel().toLowerCase()} requests are approved or rejected`
      }
    ];

    const switchStyles = {
      '& .MuiSwitch-switchBase.Mui-checked': {
        color: '#6318C6',
        '&:hover': {
          backgroundColor: 'rgba(99, 24, 198, 0.08)'
        }
      },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#6318C6'
      }
    };

    return (
      <Card elevation={0} sx={{ boxShadow: 'none', border: { xs: 'none', sm: 'none', md: '1px solid #e5e7eb' }, borderRadius: '8px' }}>
        <CardContent sx={{
          padding: { xs: '16px', sm: '16px', md: '16px' },
          '&:last-child': { paddingBottom: { xs: '16px', sm: '16px', md: '16px' } }
        }}>
          <Typography variant="h6" fontSize={{ xs: "18px", sm: "20px", md: "27px" }} gutterBottom>
            Notification Preferences
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Manage how you receive notifications about your account and AI generations.
          </Typography>

          <Box mt={3}>
            {/* Desktop/Tablet Layout */}
            <Box sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}>
              {/* Header Row */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 120px 120px', // Email and Push columns
                  gap: 2,
                  py: 2,
                  px: 2,
                  borderBottom: '2px solid #e5e7eb',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px 8px 0 0'
                }}
              >
                <Typography variant="body1" fontWeight={600} color="text.primary">
                  {/* Notification Type */}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                  <Email sx={{ fontSize: 16, color: '#6318C6' }} />
                  <Typography variant="body1" fontWeight={600} color="text.primary" textAlign="center">
                    Email
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                  <Notifications sx={{ fontSize: 16, color: '#6318C6' }} />
                  <Typography variant="body1" fontWeight={600} color="text.primary" textAlign="center">
                    Push
                  </Typography>
                </Box>
              </Box>

              {/* Notification Rows */}
              <Box sx={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
                {notificationTypes.map((notif, index) => (
                  <Box
                    key={notif.key}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 120px 120px', // Email and Push columns
                      gap: 2,
                      py: 3,
                      px: 2,
                      borderBottom: index < notificationTypes.length - 1 ? '1px solid #e5e7eb' : 'none',
                      '&:hover': { backgroundColor: '#fafbfc' },
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    {/* Left Column - Notification Info */}
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          color: '#6318C6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(99, 24, 198, 0.1)'
                        }}
                      >
                        {notif.icon}
                      </Box>
                      <Box>
                        <Typography variant="body1" fontWeight={500} gutterBottom sx={{ mb: 0.5 }}>
                          {notif.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px', lineHeight: 1.4 }}>
                          {notif.description}
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="center" justifyContent="center">
                      <Switch
                        checked={notifications[`${notif.key}_email`] || false}
                        onChange={(e) => handleNotificationUpdate(`${notif.key}_email`, e.target.checked)}
                        sx={switchStyles}
                        size="medium"
                      />
                    </Box>

                    {/* Push Column */}
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <Switch
                        checked={notifications[`${notif.key}_push`] || false}
                        onChange={(e) => handleNotificationUpdate(`${notif.key}_push`, e.target.checked)}
                        sx={switchStyles}
                        size="medium"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Mobile Layout */}
            <Box sx={{ display: { xs: 'block', sm: 'block', md: 'none' } }}>
              {notificationTypes.map((notif, index) => (
                <Box key={notif.key} sx={{ mb: 3 }}>
                  <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid #e5e7eb' }}>
                    <CardContent sx={{ p: 2.5 }}>
                      {/* Notification Header */}
                      <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <Box
                          sx={{
                            color: '#6318C6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(99, 24, 198, 0.1)'
                          }}
                        >
                          {notif.icon}
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body1" fontWeight={500} gutterBottom sx={{ mb: 0.5 }}>
                            {notif.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                            {notif.description}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Toggle Options */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          pt: 2,
                          borderTop: '1px solid #f0f0f0'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 1,
                            border: '1px solid #e9ecef',
                            width: '100%'
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <Email sx={{ fontSize: 16, color: '#6318C6' }} />
                            <Typography variant="body2" fontWeight={500}>
                              Email Notification
                            </Typography>
                          </Box>
                          <Switch
                            checked={notifications[`${notif.key}_email`] || false}
                            onChange={(e) => handleNotificationUpdate(`${notif.key}_email`, e.target.checked)}
                            sx={switchStyles}
                            size="small"
                          />
                        </Box>

                        {/* Push Toggle */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 1,
                            border: '1px solid #e9ecef',
                            width: '100%'
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <Notifications sx={{ fontSize: 16, color: '#6318C6' }} />
                            <Typography variant="body2" fontWeight={500}>
                              Push Notification
                            </Typography>
                          </Box>
                          <Switch
                            checked={notifications[`${notif.key}_push`] || false}
                            onChange={(e) => handleNotificationUpdate(`${notif.key}_push`, e.target.checked)}
                            sx={switchStyles}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderSecurityTab = () => (
    <Card elevation={0} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
      <CardContent>
        <Typography variant="h6" fontSize={{ xs: "18px", sm: "20px", md: "27px" }} gutterBottom>
          Security Settings
        </Typography>
        {/* <Typography variant="body2" color="text.secondary" gutterBottom>
          Manage your account security and password.
        </Typography> */}

        <Box mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Change Password
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last changed: {profile.password_updated_at
                      ? new Date(profile.password_updated_at).toLocaleDateString('en-GB')
                      : 'Never'
                    }
                  </Typography>

                  {/* Button to show password change form */}
                  {canSecurity && !showPasswordForm && (
                    <Button
                      variant="contained"
                      startIcon={<Security />}
                      onClick={() => setShowPasswordForm(true)}
                      sx={{
                        mt: 2,
                        backgroundColor: '#6318C6',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#7c3aed',
                        }
                      }}
                    >
                      Change Password
                    </Button>
                  )}

                  {/* Password change form - now inline instead of in dialog */}
                  {canSecurity && showPasswordForm && (
                    <Box mt={2}>
                      {/* Hidden form with autocomplete off to prevent browser from saving passwords */}
                      <form autoComplete="off" style={{ display: 'none' }} aria-hidden="true">
                        <input type="text" name="username" />
                        <input type="password" name="password" />
                      </form>

                      {/* Error Alert */}
                      {passwordError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {passwordError}
                        </Alert>
                      )}

                      {/* Password fields with responsive width */}
                      <Box
                        component="div"
                        role="presentation"
                        sx={{
                          maxWidth: { xs: '100%', sm: '100%', md: '50%' }
                        }}
                      >
                        {/* Current Password Field */}
                        <TextField
                          fullWidth
                          type={showCurrentPassword ? 'text' : 'password'}
                          label="Current Password"
                          value={passwordData.current_password}
                          onChange={(e) => handlePasswordInputChange('current_password', e.target.value)}
                          autoComplete="off"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                  edge="end"
                                  sx={{ color: '#6b7280' }}
                                >
                                  {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            mt: 2,
                            mb: 2,
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '14px', sm: '14px', md: '16px' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '14px', sm: '14px', md: '16px' }
                            },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#6318C6'
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#6318C6'
                            }
                          }}
                        />

                        {/* New Password Field */}
                        <TextField
                          fullWidth
                          type={showNewPassword ? 'text' : 'password'}
                          label="New Password"
                          value={passwordData.new_password}
                          onChange={(e) => handlePasswordInputChange('new_password', e.target.value)}
                          autoComplete="off"
                          name="new-pw-2"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowNewPassword(!showNewPassword)}
                                  edge="end"
                                  sx={{ color: '#6b7280' }}
                                >
                                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            mb: 2,
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '14px', sm: '14px', md: '16px' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '14px', sm: '14px', md: '16px' }
                            },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#6318C6'
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#6318C6'
                            }
                          }}
                        />

                        {/* Confirm New Password Field */}
                        <TextField
                          fullWidth
                          type={showConfirmPassword ? 'text' : 'password'}
                          label="Confirm New Password"
                          value={passwordData.confirm_password}
                          onChange={(e) => handlePasswordInputChange('confirm_password', e.target.value)}
                          autoComplete="off"
                          name="new-pw-3"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  edge="end"
                                  sx={{ color: '#6b7280' }}
                                >
                                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            mb: 2,
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '14px', sm: '14px', md: '16px' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '14px', sm: '14px', md: '16px' }
                            },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#6318C6'
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#6318C6'
                            }
                          }}
                        />
                      </Box>

                      {/* Password Requirements */}
                      <Box sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: '#f9fafb',
                        borderRadius: 1,
                        border: '1px solid #e5e7eb',
                        mb: 2,
                        maxWidth: { xs: '100%', sm: '100%', md: '50%' }
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                          Password Requirements:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', mb: 0.5 }}>
                          • At least 8 characters long
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', mb: 0.5 }}>
                          • Must be different from current password
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                          • New password and confirm password must match
                        </Typography>
                      </Box>

                      {/* Action Buttons */}
                      <Box sx={{
                        maxWidth: { xs: '100%', sm: '100%', md: '50%' },
                        display: 'flex',
                        gap: 2
                      }}>
                        {/* Change Password Button */}
                        <Button
                          variant="contained"
                          onClick={handlePasswordChange}
                          disabled={loading || !passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password}
                          sx={{
                            flex: 1,
                            backgroundColor: '#6318C6',
                            color: 'white',
                            fontWeight: 'bold',
                            px: 2,
                            textTransform: 'capitalize',
                            borderRadius: 1.5,
                            boxShadow: 'none',
                            '&:hover': {
                              backgroundColor: '#7c3aed',
                              color: 'white',
                            },
                            '&:disabled': {
                              backgroundColor: '#d1d5db',
                              color: '#9ca3af',
                            },
                            '&:hover:disabled': {
                              backgroundColor: '#d1d5db',
                              cursor: 'not-allowed',
                            },
                            height: '48px',
                          }}
                        >
                          {loading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                        </Button>

                        {/* Cancel Button */}
                        <Button
                          variant="outlined"
                          onClick={() => {
                            // Clear form and hide it
                            setPasswordData({
                              current_password: '',
                              new_password: '',
                              confirm_password: ''
                            });
                            setShowCurrentPassword(false);
                            setShowNewPassword(false);
                            setShowConfirmPassword(false);
                            setPasswordError('');
                            setShowPasswordForm(false);
                          }}
                          sx={{
                            flex: 1,
                            borderColor: '#d1d5db',
                            color: '#374151',
                            fontWeight: 'bold',
                            px: 2,
                            textTransform: 'capitalize',
                            borderRadius: 1.5,
                            '&:hover': {
                              borderColor: '#9ca3af',
                              backgroundColor: '#f9fafb',
                            },
                            height: '48px',
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Delete Account Section - keeping this */}
          <Box mt={4}>
            <Card variant="outlined" sx={{ borderColor: '#ef4444', backgroundColor: '#fef2f2' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="error">
                  Delete Account
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  This action cannot be undone. Once you delete your account, all your data will be permanently removed.
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteAccountDialog(true)}
                  sx={{
                    mt: 2,
                    borderColor: '#ef4444',
                    color: '#ef4444',
                    '&:hover': {
                      borderColor: '#dc2626',
                      backgroundColor: '#fef2f2',
                      color: '#dc2626'
                    }
                  }}
                >
                  Delete Account
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{
      mt: { xs: 0, sm: 2, md: 4 },
      mb: { xs: 0, sm: 2, md: 4 },
      px: { xs: 0, sm: 2, md: 3 }
    }}>
      <Paper elevation={0} sx={{
        p: { xs: 0, sm: 2, md: 4 },
        boxShadow: 'none',
        border: { xs: 'none', sm: '1px solid #e5e7eb', md: '1px solid #e5e7eb' },
        bgcolor: { xs: 'transparent', sm: 'white', md: 'white' },
        borderRadius: '8px'
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 3, sm: 2, md: 2 }}>
          <Typography variant="h6" fontWeight={600} fontSize={{ xs: "18px", sm: "20px", md: "27px" }}>
            Profile Settings
          </Typography>
          <Button
            onClick={() => navigate('/dashboard')}
            startIcon={<ArrowBack />}
            sx={{
              background: '#6318C6',
              color: 'white',
              borderRadius: 2,
              px: 2,
              py: 1,
              transition: 'all 0.3s ease',
              textTransform: 'none',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '&:hover': {
                background: '#5a4db2',
                backgroundColor: '#5a4db2'
              }
            }}
          >
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              Back to Dashboard
            </Box>
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              Back
            </Box>
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: { xs: 2, sm: 3, md: 3 } }}>
            {error}
          </Alert>
        )}

        <AutoCloseAlert
          severity="success"
          message={success}
          show={!!success}
          onClose={() => setSuccess('')}
          autoCloseDelay={4000}
        />

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3, md: 3 } }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{
              '& .MuiTabs-scrollButtons': {
                display: { xs: 'flex', sm: 'flex', md: 'none' }
              },
              '& .MuiTabs-flexContainer': {
                justifyContent: 'flex-start'
              },
              '& .MuiTab-root': {
                minHeight: { xs: '40px', sm: '40px', md: '48px' },
                padding: { xs: '6px 12px', sm: '6px 12px', md: '12px 16px' },
                fontWeight: 600,
                fontSize: 15,
                textTransform: 'capitalize',
                bgcolor: 'transparent',
                color: '#64748b',
                borderRadius: 2,
                mx: 0.5,
                transition: 'all 0.3s ease',
                '&.Mui-selected': {
                  bgcolor: 'rgba(99, 24, 198, 0.15)',
                  color: '#6318C6',
                  // boxShadow: '0 2px 8px 0 rgba(99, 24, 198, 0.3)',
                  borderRadius: '8px 8px 0px 0px',
                  '& .MuiSvgIcon-root': {
                    color: '#6318C6 !important',
                  },
                  '& .MuiTab-label': {
                    color: '#6318C6 !important',
                  },
                  '& span': {
                    color: '#6318C6 !important',
                  },
                },
                '&:hover': {
                  bgcolor: 'rgba(241, 245, 249, 0.8)',
                  transform: 'none',
                  boxShadow: 'none',
                  '& .MuiSvgIcon-root': {
                    color: '#64748b !important',
                  },
                  '& .MuiTab-label': {
                    color: '#64748b !important',
                  },
                  '& span': {
                    color: '#64748b !important',
                  },
                },
                '&.Mui-selected:hover': {
                  bgcolor: 'rgba(99, 24, 198, 0.25)',
                  transform: 'translateY(-1px)',
                  // boxShadow: '0 4px 12px 0 rgba(99, 24, 198, 0.4)',
                  '& .MuiSvgIcon-root': {
                    color: '#6318C6 !important',
                  },
                  '& .MuiTab-label': {
                    color: '#6318C6 !important',
                  },
                  '& span': {
                    color: '#6318C6 !important',
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: '#64748b !important',
                },
                '& .MuiTab-label': {
                  color: '#64748b !important',
                },
                '& span': {
                  color: '#64748b !important',
                },
              },
              '& .MuiTabs-indicator': {
                display: 'none'
              }
            }}
          >
            <Tab icon={<Person />} label="Profile" iconPosition="start" />
            <Tab icon={<Storefront />} label="Brands" iconPosition="start" />
            <Tab icon={<Notifications />} label="Notifications" iconPosition="start" />
            <Tab icon={<Security />} label="Security" iconPosition="start" />
          </Tabs>
        </Box>

        {activeTab === 0 && renderProfileTab()}
        {activeTab === 1 && renderBrandLogoTab()}
        {activeTab === 2 && renderNotificationsTab()}
        {activeTab === 3 && renderSecurityTab()}
      </Paper>

      {/* Delete Account Confirmation Dialog - keeping this */}
      <Dialog
        open={deleteAccountDialog}
        onClose={() => setDeleteAccountDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ px: 3, py: 2, borderBottom: '1px solid #e5e7eb', color: '#ef4444' }}>
          Delete Account
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              This action cannot be undone!
            </Typography>
            <Typography variant="body2">
              Once you delete your account, all your data including:
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Profile information</li>
              <li>AI generations</li>
              <li>{FlutterServicesAPI.getCreditLabel()} balance</li>
              <li>Brand logos</li>
              <li>All other account data</li>
            </ul>
            <Typography variant="body2" fontWeight="bold">
              Will be permanently removed and cannot be recovered.
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete your account? This action will also log you out immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          flexDirection: { xs: 'column', sm: 'row', md: 'row' },
          gap: { xs: 2, sm: 1, md: 1 },
          width: { xs: '100%', sm: '100%', md: 'auto' },
          px: 3,
          py: 2,
          borderTop: '1px solid #e5e7eb'
        }}>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAccount}
            disabled={deleteAccountLoading}
            sx={{
              backgroundColor: '#ef4444',
              color: 'white',
              fontWeight: 'bold',
              px: 4,
              textTransform: 'capitalize',
              borderRadius: 1.5,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#dc2626',
                color: 'white',
              },
              '&:disabled': {
                backgroundColor: '#fca5a5',
                color: '#fef2f2',
              },
              height: { xs: '48px', sm: '48px', md: '48px' },
              width: { xs: '100%', sm: '100%', md: 'auto' },
              order: { xs: 1, sm: 1, md: 2 }
            }}
          >
            {deleteAccountLoading ? <CircularProgress size={20} color="inherit" /> : 'Yes, Delete Account'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setDeleteAccountDialog(false)}
            sx={{
              borderColor: '#d1d5db',
              color: '#374151',
              fontWeight: 'bold',
              textTransform: 'capitalize',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f9fafb',
                color: '#374151'
              },
              height: { xs: '48px', sm: '48px', md: '48px' },
              width: { xs: '100%', sm: '100%', md: 'auto' },
              order: { xs: 2, sm: 2, md: 1 },
              mr: { xs: 0, sm: 0, md: 'auto' },
              ml: { xs: 0, sm: 0, md: 1 }
            }}
          >
            No, Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Brand Logo Dialog - Professional, User-friendly & Responsive */}
      <Dialog
        open={brandLogoDialog}
        onClose={() => {
          setBrandLogoDialog(false);
          setBrandDialogError(''); // Clear brand dialog errors
          // Reset variant progress and status
          setVariantProgress({});
          setVariantStatus({});
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={{ xs: true, sm: false }}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            boxShadow: {
              xs: 'none',
              sm: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            },
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: '32px' },
            width: { xs: '100%', sm: '800px' },
            maxWidth: '800px',
            minWidth: { xs: '100%', sm: '600px' }
          }
        }}
        aria-labelledby="brand-logo-dialog-title"
        aria-describedby="brand-logo-dialog-description"
      >
        <DialogTitle
          id="brand-logo-dialog-title"
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            borderBottom: '1px solid #e5e7eb',
            background: 'linear-gradient(135deg, #6318C6 0%, #7c3aed 100%)',
            color: 'white',
            position: 'relative'
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 1.5 },
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Box sx={{
              width: { xs: 28, sm: 32 },
              height: { xs: 28, sm: 32 },
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <PhotoCamera sx={{ color: 'white', fontSize: { xs: 16, sm: 18 } }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'white',
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  lineHeight: 1.2
                }}
              >
                {editingBrandLogo ? 'Edit Brand Logo' : 'Add Brand Logo'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  display: { xs: 'block', sm: 'block' },
                  mt: 0.5
                }}
              >
                Upload your brand logo for AI-powered auto-generation and processing
              </Typography>
            </Box>
            {/* Close button for mobile */}
            <IconButton
              onClick={() => {
                setBrandLogoDialog(false);
                setBrandDialogError('');
              }}
              sx={{
                display: { xs: 'flex', sm: 'none' },
                color: 'white',
                p: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
              aria-label="Close dialog"
            >
              <Cancel sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          id="brand-logo-dialog-description"
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            overflow: 'auto',
            maxHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(90vh - 200px)' }
          }}
        >
          {brandDialogError && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontSize: { xs: '0.875rem', sm: '0.9rem' }
                }
              }}
              role="alert"
            >
              {brandDialogError}
            </Alert>
          )}


          {/* Step 1: Brand Name */}
          <Box sx={{ mt: 5, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: '#6318C6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Typography sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}>1</Typography>
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  color: '#374151',
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.1rem' }
                }}
              >
                Brand Information
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Brand Name"
              placeholder="Enter your brand name (e.g., My Jewelry Store)"
              value={brandLogoData.brand_name}
              onChange={(e) => setBrandLogoData({ ...brandLogoData, brand_name: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6318C6',
                  borderWidth: 2
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#6318C6'
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                }
              }}
              required
              error={!brandLogoData.brand_name.trim() && brandLogoData.brand_name !== ''}
              helperText={!brandLogoData.brand_name.trim() && brandLogoData.brand_name !== '' ? 'Brand name is required' : ''}
              inputProps={{
                'aria-describedby': 'brand-name-helper-text'
              }}
            />
          </Box>

          {/* Step 2: Logo Upload */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: '#6318C6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Typography sx={{ color: 'white', fontSize: '0.7rem', fontWeight: 'bold' }}>2</Typography>
              </Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#374151',
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                }}
              >
                Upload Logo
              </Typography>
            </Box>

            <input
              accept=".jpg,.jpeg,.png,.webp,.heic,image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
              style={{ display: 'none' }}
              id="logo-upload"
              type="file"
              onChange={handleFileSelect}
              aria-describedby="logo-upload-helper"
            />

            {/* Compact Upload Button */}
            <Button
              variant="outlined"
              startIcon={<PhotoCamera />}
              onClick={handleLogoUploadClick}
              disabled={uploadingLogo}
              sx={{
                borderColor: '#6318C6',
                color: '#6318C6',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                px: 3,
                py: 1,
                borderRadius: 1,
                fontSize: '0.875rem',
                '&:hover': {
                  borderColor: '#6318C6',
                  backgroundColor: '#f3e8ff'
                }
              }}
            >
              {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
            </Button>

            {/* Compact helper text */}
            <Typography
              id="logo-upload-helper"
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                color: '#6b7280',
                fontSize: '0.75rem'
              }}
            >
              Accepted formats: JPG, JPEG, PNG, WebP, HEIC (Max 6MB) - Required
            </Typography>
          </Box>


          {/* Step 3: Preview */}
          {selectedFile && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2
              }}>
                <Box sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: '#6318C6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Typography sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}>3</Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#374151',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.1rem' }
                  }}
                >
                  Preview Results
                </Typography>
              </Box>

              {/* Progress Bar */}
              {previewLoading && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 1
                  }}>
                    <CircularProgress size={20} sx={{ color: '#6318C6' }} />
                    <Typography variant="body2" sx={{ color: '#6318C6', fontWeight: 500 }}>
                      Processing your logo with AI...
                    </Typography>
                  </Box>
                  <LinearProgress
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: '#f3e8ff',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#6318C6',
                        borderRadius: 3
                      }
                    }}
                  />
                </Box>
              )}

              {showPreview && previewData && (
                <Box sx={{
                  p: { xs: 2, sm: 3 },
                  bgcolor: '#f8f9fa',
                  borderRadius: 3,
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    {/* Image Variants Selection */}
                    {previewData.processed && previewData.processed.variants && (
                      <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                            <Box sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: '#6318C6',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Typography sx={{ color: 'white', fontSize: '0.8rem' }}>🎨</Typography>
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: '#374151',
                                fontSize: { xs: '1.1rem', sm: '1.25rem' }
                              }}
                            >
                              Choose Your Preferred Image
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 3,
                              fontSize: { xs: '0.85rem', sm: '0.9rem' },
                              maxWidth: '600px',
                              mx: 'auto'
                            }}
                          >
                            Select from the original or AI-processed variants below
                          </Typography>
                        </Box>

                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                          {/* Original Image */}
                          <Grid item xs={6} sm={4} md={3}>
                            <Box
                              sx={{
                                textAlign: 'center',
                                cursor: 'pointer',
                                p: { xs: 1, sm: 1.5 },
                                borderRadius: 3,
                                border: selectedImageVariant === 0 ? '3px solid #6318C6' : '2px solid #e5e7eb',
                                backgroundColor: selectedImageVariant === 0 ? '#f3f0ff' : 'white',
                                transition: 'all 0.3s ease',
                                position: 'relative',
                                overflow: 'hidden',
                                height: { xs: 280, sm: 300 },
                                display: 'flex',
                                flexDirection: 'column',
                                '&:hover': {
                                  borderColor: '#6318C6',
                                  backgroundColor: '#f9f5ff',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(99, 24, 198, 0.15)'
                                }
                              }}
                              onClick={() => setSelectedImageVariant(0)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedImageVariant(0);
                                }
                              }}
                              aria-label="Select original image"
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  mb: 1,
                                  color: '#374151',
                                  fontSize: { xs: '0.8rem', sm: '0.9rem' }
                                }}
                              >
                                Original
                              </Typography>
                              <Box
                                sx={{
                                  width: '100%',
                                  height: { xs: 100, sm: 120 },
                                  borderRadius: 2,
                                  border: '1px solid #e5e7eb',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  overflow: 'hidden',
                                  backgroundColor: '#f9fafb',
                                  position: 'relative'
                                }}
                              >
                                {/* Individual Progress Indicator for Original */}
                                {variantStatus.original === 'loading' && (
                                  <Box sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    zIndex: 2
                                  }}>
                                    <CircularProgress
                                      size={24}
                                      sx={{ color: '#6318C6', mb: 1 }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: '#6318C6',
                                        fontWeight: 500,
                                        fontSize: '0.7rem'
                                      }}
                                    >
                                      {Math.round(variantProgress.original || 0)}%
                                    </Typography>
                                    <LinearProgress
                                      variant="determinate"
                                      value={variantProgress.original || 0}
                                      sx={{
                                        width: '80%',
                                        mt: 1,
                                        height: 4,
                                        borderRadius: 2,
                                        backgroundColor: '#f3e8ff',
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor: '#6318C6',
                                          borderRadius: 2
                                        }
                                      }}
                                    />
                                  </Box>
                                )}

                                {/* Error State for Original */}
                                {variantStatus.original === 'error' && (
                                  <Box sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    zIndex: 2
                                  }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: '#ef4444',
                                        fontWeight: 500,
                                        fontSize: '0.7rem',
                                        textAlign: 'center'
                                      }}
                                    >
                                      Error loading
                                    </Typography>
                                  </Box>
                                )}

                                {/* Image Display */}
                                {variantStatus.original === 'completed' && (
                                  <img
                                    src={previewData.original.data}
                                    alt="Original logo"
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '100%',
                                      objectFit: 'contain'
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  mt: 0.5,
                                  display: 'block',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }}
                              >
                                {(previewData.original.size / 1024).toFixed(1)} KB
                              </Typography>
                              {selectedImageVariant === 0 && (
                                <Chip
                                  label="Selected"
                                  size="small"
                                  color="primary"
                                  sx={{
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    height: { xs: 18, sm: 20 },
                                    mt: 1,
                                    fontWeight: 600
                                  }}
                                />
                              )}
                            </Box>
                          </Grid>

                          {/* Processed Variants */}
                          {previewData.processed.variants.map((variant, index) => {
                            const variantKey = `variant${variant.index + 1}`;
                            const isVariantLoading = variantStatus[variantKey] === 'loading';
                            const isVariantCompleted = variantStatus[variantKey] === 'completed';
                            const isVariantError = variantStatus[variantKey] === 'error';

                            return (
                              <Grid item xs={6} sm={4} md={3} key={variant.index}>
                                <Box
                                  sx={{
                                    textAlign: 'center',
                                    cursor: isVariantLoading ? 'not-allowed' : 'pointer',
                                    p: { xs: 1, sm: 1.5 },
                                    borderRadius: 3,
                                    border: selectedImageVariant === (variant.index + 1) ? '3px solid #6318C6' : '2px solid #e5e7eb',
                                    backgroundColor: selectedImageVariant === (variant.index + 1) ? '#f3f0ff' : 'white',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    height: { xs: 280, sm: 300 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    opacity: isVariantLoading ? 0.7 : 1,
                                    '&:hover': !isVariantLoading ? {
                                      borderColor: '#6318C6',
                                      backgroundColor: '#f9f5ff',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 4px 12px rgba(99, 24, 198, 0.15)'
                                    } : {}
                                  }}
                                  onClick={() => !isVariantLoading && setSelectedImageVariant(variant.index + 1)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (!isVariantLoading && (e.key === 'Enter' || e.key === ' ')) {
                                      e.preventDefault();
                                      setSelectedImageVariant(variant.index + 1);
                                    }
                                  }}
                                  aria-label={`Select ${variant.name} variant`}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      mb: 1,
                                      color: '#374151',
                                      fontSize: { xs: '0.8rem', sm: '0.9rem' }
                                    }}
                                  >
                                    {variant.name}
                                    {isVariantLoading && (
                                      <Typography
                                        component="span"
                                        sx={{
                                          color: '#6318C6',
                                          fontSize: '0.7rem',
                                          ml: 1
                                        }}
                                      >
                                        (Processing...)
                                      </Typography>
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      mb: 1,
                                      display: 'block',
                                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                      lineHeight: 1.3
                                    }}
                                  >
                                    {variant.description}
                                  </Typography>
                                  <Box
                                    sx={{
                                      width: '100%',
                                      height: { xs: 100, sm: 120 },
                                      borderRadius: 2,
                                      border: '1px solid #e5e7eb',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      overflow: 'hidden',
                                      backgroundColor: '#f9fafb',
                                      backgroundImage: isVariantCompleted ? `
                                        linear-gradient(45deg, #f3f4f6 25%, transparent 25%),
                                        linear-gradient(-45deg, #f3f4f6 25%, transparent 25%),
                                        linear-gradient(45deg, transparent 75%, #f3f4f6 75%),
                                        linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)
                                      ` : 'none',
                                      backgroundSize: '20px 20px',
                                      backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                                      position: 'relative'
                                    }}
                                  >
                                    {/* Individual Progress Indicator for Variant */}
                                    {isVariantLoading && (
                                      <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        zIndex: 2
                                      }}>
                                        <CircularProgress
                                          size={24}
                                          sx={{ color: '#6318C6', mb: 1 }}
                                        />
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: '#6318C6',
                                            fontWeight: 500,
                                            fontSize: '0.7rem'
                                          }}
                                        >
                                          {Math.round(variantProgress[variantKey] || 0)}%
                                        </Typography>
                                        <LinearProgress
                                          variant="determinate"
                                          value={variantProgress[variantKey] || 0}
                                          sx={{
                                            width: '80%',
                                            mt: 1,
                                            height: 4,
                                            borderRadius: 2,
                                            backgroundColor: '#f3e8ff',
                                            '& .MuiLinearProgress-bar': {
                                              backgroundColor: '#6318C6',
                                              borderRadius: 2
                                            }
                                          }}
                                        />
                                      </Box>
                                    )}

                                    {/* Error State for Variant */}
                                    {isVariantError && (
                                      <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        zIndex: 2
                                      }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: '#ef4444',
                                            fontWeight: 500,
                                            fontSize: '0.7rem',
                                            textAlign: 'center'
                                          }}
                                        >
                                          Error processing
                                        </Typography>
                                      </Box>
                                    )}

                                    {/* Image Display */}
                                    {isVariantCompleted && (
                                      <img
                                        src={variant.data}
                                        alt={`${variant.name} logo variant`}
                                        style={{
                                          maxWidth: '100%',
                                          maxHeight: '100%',
                                          objectFit: 'contain'
                                        }}
                                      />
                                    )}
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      mt: 0.5,
                                      display: 'block',
                                      fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                    }}
                                  >
                                    {(variant.size / 1024).toFixed(1)} KB
                                  </Typography>
                                  <Box sx={{
                                    mt: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: 0.5,
                                    flexWrap: 'wrap',
                                    mb: 1
                                  }}>
                                    {previewData.processed.background_removed && (
                                      <Chip
                                        label="BG Removed"
                                        size="small"
                                        color="success"
                                        sx={{
                                          fontSize: { xs: '0.55rem', sm: '0.6rem' },
                                          height: { xs: 16, sm: 18 },
                                          fontWeight: 600
                                        }}
                                      />
                                    )}
                                    {previewData.processed.image_enhanced && (
                                      <Chip
                                        label="Enhanced"
                                        size="small"
                                        color="primary"
                                        sx={{
                                          fontSize: { xs: '0.55rem', sm: '0.6rem' },
                                          height: { xs: 16, sm: 18 },
                                          fontWeight: 600
                                        }}
                                      />
                                    )}
                                  </Box>
                                  {selectedImageVariant === (variant.index + 1) && (
                                    <Chip
                                      label="Selected"
                                      size="small"
                                      color="primary"
                                      sx={{
                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                        height: { xs: 18, sm: 20 },
                                        mt: 1,
                                        fontWeight: 600
                                      }}
                                    />
                                  )}
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Grid>
                    )}


                    {/* Error Message */}
                    {previewData.error && (
                      <Grid item xs={12}>
                        <Alert severity="error" sx={{ borderRadius: 1, py: 1 }}>
                          {previewData.error}
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Box>
          )}

          {/* Current Logo Display */}
          {brandLogoData.brand_logo_path && (
            <Box sx={{
              p: 2,
              bgcolor: '#f0f9ff',
              borderRadius: 2,
              border: '1px solid #bae6fd'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#0369a1' }}>
                Current Logo
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 45,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid #0ea5e9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'white'
                  }}
                >
                  <SecureImage
                    src={getImageUrl(brandLogoData.brand_logo_path)}
                    alt="Brand Logo"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      console.error('Failed to load brand logo:', e.target.src);
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  This will be replaced with your new logo
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          borderTop: '1px solid #e5e7eb',
          bgcolor: '#fafafa',
          position: 'sticky',
          bottom: 0,
          zIndex: 1
        }}>
          <Box sx={{
            display: 'flex',
            gap: { xs: 1.5, sm: 2 },
            width: '100%',
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              variant="outlined"
              onClick={() => setBrandLogoDialog(false)}
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                fontWeight: 600,
                px: { xs: 3, sm: 3 },
                py: { xs: 1.5, sm: 1.5 },
                borderRadius: 2,
                textTransform: 'none',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                minHeight: { xs: '48px', sm: '40px' },
                order: { xs: 2, sm: 1 },
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb',
                  color: '#374151',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleBrandLogoSubmit}
              disabled={loading || uploadingLogo || !brandLogoData.brand_name.trim() || !selectedFile}
              sx={{
                backgroundColor: '#6318C6',
                color: 'white',
                fontWeight: 600,
                px: { xs: 4, sm: 4 },
                py: { xs: 1.5, sm: 1.5 },
                borderRadius: 2,
                textTransform: 'none',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                minHeight: { xs: '48px', sm: '40px' },
                order: { xs: 1, sm: 2 },
                boxShadow: '0 4px 6px -1px rgba(99, 24, 198, 0.1), 0 2px 4px -1px rgba(99, 24, 198, 0.06)',
                '&:hover': {
                  backgroundColor: '#7c3aed',
                  boxShadow: '0 10px 15px -3px rgba(99, 24, 198, 0.1), 0 4px 6px -2px rgba(99, 24, 198, 0.05)',
                  transform: 'translateY(-1px)'
                },
                '&:disabled': {
                  backgroundColor: '#d1d5db',
                  color: '#9ca3af',
                  boxShadow: 'none',
                  transform: 'none'
                },
                '&:hover:disabled': {
                  backgroundColor: '#d1d5db',
                  cursor: 'not-allowed',
                  transform: 'none'
                }
              }}
            >
              {uploadingLogo ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                  <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                    {editingBrandLogo ? 'Updating...' : 'Creating...'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhotoCamera sx={{ fontSize: { xs: 16, sm: 18 } }} />
                  <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                    {editingBrandLogo ? 'Update Brand' : 'Create Brand'}
                  </Typography>
                </Box>
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Share Referral Code Dialog - keeping this */}
      <Dialog
        open={shareDialog}
        onClose={() => setShareDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ px: 3, py: 2, borderBottom: '1px solid #e5e7eb', position: 'relative' }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              fontWeight: 600
            }}
          >
            Share Your Referral Code
          </Typography>
          <IconButton
            onClick={() => setShareDialog(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#6b7280',
              width: { xs: 32, sm: 36, md: 48 },
              height: { xs: 32, sm: 36, md: 48 },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '30px', sm: '30px', md: '38px' }
              }
            }}
          >
            <Cancel />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3, mt: 3 }}>
            {/* Referral Code Display */}
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
              Referral Code
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 'bold',
                color: '#6318C6',
                letterSpacing: '0.15em',
                mb: 3,
                textShadow: '0 1px 2px rgba(99, 24, 198, 0.1)'
              }}
            >
              {referralCode}
            </Typography>

            {/* Full Message Display */}
            <Box
              sx={{
                p: 3,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef',
                mb: 3,
                textAlign: 'left',
                wordBreak: 'break-all',
                overflowWrap: 'break-word',
                maxWidth: '100%'
              }}
            >

              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.6,
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  color: '#374151',
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-all',
                  overflowWrap: 'break-word',
                  maxWidth: '100%'
                }}
              >
                {`Hi! I'm using Glammify to create professional model photos of my jewellery instantly. You should try it too 👉 ${getRegisterUrl(referralCode)}\nUse my code ${referralCode} for bonus ${FlutterServicesAPI.getCreditLabel().toLowerCase()}!`}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          flexDirection: { xs: 'column', sm: 'row', md: 'row' },
          gap: { xs: 2, sm: 2, md: 2 },
          width: '100%',
          px: 3,
          py: 2,
          borderTop: '1px solid #e5e7eb',
          justifyContent: { xs: 'stretch', sm: 'center', md: 'center' }
        }}>
          <Button
            variant="contained"
            startIcon={<ContentCopy />}
            onClick={() => {
              const fullMessage = `Hi! I'm using Glammify to create professional model photos of my jewellery instantly. You should try it too 👉 ${getRegisterUrl(referralCode)}\nUse my code ${referralCode} for bonus ${FlutterServicesAPI.getCreditLabel().toLowerCase()}!`;
              handleCopyReferralCode(fullMessage);
              setShareDialog(false);
            }}
            sx={{
              background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
              color: 'white',
              fontWeight: 'bold',
              px: 4,
              textTransform: 'capitalize',
              borderRadius: 1.5,
              boxShadow: 'none',
              '&:hover': {
                background: '#6318C6',
                color: 'white',
              },
              height: '48px',
              width: { xs: '100%', sm: 'auto', md: 'auto' },
              maxWidth: { xs: '100%', sm: '200px', md: '200px' }
            }}
          >
            Copy Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Brand Logo Confirmation Dialog */}
      <Dialog
        open={deleteBrandLogoDialog}
        onClose={() => setDeleteBrandLogoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ px: 3, py: 2, borderBottom: '1px solid #e5e7eb', color: '#ef4444' }}>
          Delete Brand Logo
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              Are you sure you want to delete this brand logo?
            </Typography>
            <Typography variant="body2">
              This action cannot be undone. The brand logo will be permanently removed from your account.
            </Typography>
          </Alert>

          {brandLogoToDelete && (
            <Box sx={{
              p: 2,
              bgcolor: '#f8f9fa',
              borderRadius: 2,
              border: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                Brand: {brandLogoToDelete.brand_name}
              </Typography>
              {brandLogoToDelete.brand_logo_path && (
                <Box
                  sx={{
                    width: 40,
                    height: 30,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid #d1d5db',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'white'
                  }}
                >
                  <SecureImage
                    src={getImageUrl(brandLogoToDelete.brand_logo_path)}
                    alt="Brand Logo"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      console.error('Failed to load brand logo:', e.target.src);
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{
          flexDirection: { xs: 'column', sm: 'row', md: 'row' },
          gap: { xs: 2, sm: 1, md: 1 },
          width: { xs: '100%', sm: '100%', md: 'auto' },
          px: 3,
          py: 2,
          borderTop: '1px solid #e5e7eb'
        }}>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteBrandLogo}
            disabled={deleteBrandLogoLoading}
            sx={{
              backgroundColor: '#ef4444',
              color: 'white',
              fontWeight: 'bold',
              px: 4,
              textTransform: 'capitalize',
              borderRadius: 1.5,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#dc2626',
                color: 'white',
              },
              '&:disabled': {
                backgroundColor: '#fca5a5',
                color: '#fef2f2',
              },
              height: { xs: '48px', sm: '48px', md: '48px' },
              width: { xs: '100%', sm: '100%', md: 'auto' },
              order: { xs: 1, sm: 1, md: 2 }
            }}
          >
            {deleteBrandLogoLoading ? <CircularProgress size={20} color="inherit" /> : 'Yes, Delete Brand Logo'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setDeleteBrandLogoDialog(false);
              setBrandLogoToDelete(null);
            }}
            sx={{
              borderColor: '#d1d5db',
              color: '#374151',
              fontWeight: 'bold',
              textTransform: 'capitalize',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f9fafb',
                color: '#374151'
              },
              height: { xs: '48px', sm: '48px', md: '48px' },
              width: { xs: '100%', sm: '100%', md: 'auto' },
              order: { xs: 2, sm: 2, md: 1 },
              mr: { xs: 0, sm: 0, md: 'auto' },
              ml: { xs: 0, sm: 0, md: 1 }
            }}
          >
            No, Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Success Notification - keeping this */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message={copyMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#4caf50',
            color: 'white',
            fontWeight: 500
          }
        }}
      />
    </Container>
  );
};

export default MerchantProfile; 